import { useState } from 'react';
import {
  Tile,
  TextInput,
  Button,
  TextArea,
  NumberInput,
  InlineNotification,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Select,
  SelectItem,
  Tag
} from '@carbon/react';
import { Add, TrashCan, Download, Document, Currency, Send } from '@carbon/icons-react';
import CarbonDateField from '@/components/CarbonDateField';
import { useAuth } from '@/contexts/AuthContext';
import { createDocument, getPublicUrl } from '@/lib/documentsApi';

const InvoiceGenerator = ({ onSent, embedded = false }) => {
  const [invoice, setInvoice] = useState({
    invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    companyName: 'SenseStack Pty Ltd',
    companyAddress: 'Australia',
    companyEmail: 'inquiry@sensestack.com.au',
    companyPhone: '+61 (03) 9010 6148',
    clientName: '',
    clientAddress: '',
    clientEmail: '',
    notes: '',
    items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
    subtotal: 0,
    taxRate: 10,
    taxAmount: 0,
    total: 0,
    currency: 'AUD'
  });
  
  const [showPreview, setShowPreview] = useState(false);
  const [notification, setNotification] = useState(null);
  const { user } = useAuth();

  const currencies = [
    { value: 'AUD', label: 'Australian Dollar (AUD)' },
    { value: 'USD', label: 'US Dollar (USD)' },
    { value: 'EUR', label: 'Euro (EUR)' },
    { value: 'GBP', label: 'British Pound (GBP)' },
    { value: 'NZD', label: 'New Zealand Dollar (NZD)' }
  ];

  const updateItem = (index, field, value) => {
    const newItems = [...invoice.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }
    
    calculateTotals(newItems);
    setInvoice({ ...invoice, items: newItems });
  };

  const addItem = () => {
    setInvoice({
      ...invoice,
      items: [...invoice.items, { description: '', quantity: 1, rate: 0, amount: 0 }]
    });
  };

  const removeItem = (index) => {
    if (invoice.items.length === 1) return;
    const newItems = invoice.items.filter((_, i) => i !== index);
    calculateTotals(newItems);
    setInvoice({ ...invoice, items: newItems });
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = (subtotal * invoice.taxRate) / 100;
    const total = subtotal + taxAmount;
    
    setInvoice(prev => ({
      ...prev,
      subtotal,
      taxAmount,
      total
    }));
  };

  const formatCurrency = (amount) => {
    const symbols = {
      AUD: '$',
      USD: '$',
      EUR: '€',
      GBP: '£',
      NZD: '$'
    };
    return `${symbols[invoice.currency] || '$'}${amount.toFixed(2)}`;
  };

  const generatePDF = () => {
    // Create a printable HTML version
    const printWindow = window.open('', '_blank');
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoiceNumber}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700&display=swap');
          body { font-family: 'Noto Sans', sans-serif; margin: 40px; color: #333; }
          .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .company-info { text-align: right; }
          .invoice-title { font-size: 32px; font-weight: 700; color: #0f62fe; margin-bottom: 10px; }
          .details { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .details-section h3 { font-size: 14px; text-transform: uppercase; color: #666; margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin: 30px 0; }
          th { background: #f4f4f4; padding: 12px; text-align: left; font-weight: 600; }
          td { padding: 12px; border-bottom: 1px solid #e0e0e0; }
          .text-right { text-align: right; }
          .totals { margin-left: auto; width: 300px; margin-top: 20px; }
          .total-row { display: flex; justify-content: space-between; padding: 8px 0; }
          .grand-total { font-weight: 700; font-size: 18px; border-top: 2px solid #333; padding-top: 12px; margin-top: 12px; }
          .notes { margin-top: 40px; padding: 20px; background: #f4f4f4; }
          .footer { margin-top: 60px; text-align: center; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="invoice-title">INVOICE</div>
            <div><strong>${invoice.invoiceNumber}</strong></div>
          </div>
          <div class="company-info">
            <h2>${invoice.companyName}</h2>
            <p>${invoice.companyAddress}</p>
            <p>${invoice.companyEmail}</p>
            <p>${invoice.companyPhone}</p>
          </div>
        </div>
        
        <div class="details">
          <div class="details-section">
            <h3>Bill To:</h3>
            <p><strong>${invoice.clientName}</strong></p>
            <p>${invoice.clientAddress}</p>
            <p>${invoice.clientEmail}</p>
          </div>
          <div class="details-section">
            <h3>Invoice Details:</h3>
            <p><strong>Date:</strong> ${invoice.date}</p>
            <p><strong>Due Date:</strong> ${invoice.dueDate || 'On Receipt'}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th class="text-right">Quantity</th>
              <th class="text-right">Rate</th>
              <th class="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items.map(item => `
              <tr>
                <td>${item.description}</td>
                <td class="text-right">${item.quantity}</td>
                <td class="text-right">${formatCurrency(item.rate)}</td>
                <td class="text-right">${formatCurrency(item.amount)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>${formatCurrency(invoice.subtotal)}</span>
          </div>
          <div class="total-row">
            <span>Tax (${invoice.taxRate}%):</span>
            <span>${formatCurrency(invoice.taxAmount)}</span>
          </div>
          <div class="total-row grand-total">
            <span>Total:</span>
            <span>${formatCurrency(invoice.total)}</span>
          </div>
        </div>
        
        ${invoice.notes ? `
          <div class="notes">
            <h3>Notes:</h3>
            <p>${invoice.notes}</p>
          </div>
        ` : ''}
        
        <div class="footer">
          <p>Thank you for your business!</p>
          <p>ABN 14187234379 | inquiry@sensestack.com.au</p>
        </div>
        
        <script>window.print();</script>
      </body>
      </html>
    `;
    
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    
    setNotification({ kind: 'success', title: 'Invoice Generated', subtitle: 'Print dialog opened' });
    setTimeout(() => setNotification(null), 3000);
  };

  const sendInvoice = async () => {
    if (!invoice.clientEmail) {
      setNotification({ kind: 'error', title: 'Client email required', subtitle: 'Add a client email before sending.' });
      return;
    }

    calculateTotals(invoice.items);

    const subtotal = invoice.items.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = (subtotal * invoice.taxRate) / 100;
    const total = subtotal + taxAmount;

    try {
      const doc = await createDocument({
        type: 'invoice',
        ownerId: user?.id,
        clientEmail: invoice.clientEmail,
        clientName: invoice.clientName,
        status: 'sent',
        data: { ...invoice, subtotal, taxAmount, total },
      });
      const link = getPublicUrl('invoice', doc.publicToken);
      navigator.clipboard.writeText(link);
      setNotification({
        kind: 'success',
        title: 'Invoice sent',
        subtitle: `Payment link copied: ${link}`,
      });
      onSent?.();
    } catch (err) {
      setNotification({ kind: 'error', title: 'Send failed', subtitle: err.message });
    }
    setTimeout(() => setNotification(null), 5000);
  };

  return (
    <div className="invoice-generator">
      {!embedded && (
        <div className="section-header">
          <Tag type="blue">BUSINESS TOOLS</Tag>
          <h2 className="section-title">Invoice Generator</h2>
          <p className="section-description">Create invoices and send payment links via Stripe</p>
        </div>
      )}

      {notification && (
        <InlineNotification
          kind={notification.kind}
          title={notification.title}
          subtitle={notification.subtitle}
          lowContrast
          onClose={() => setNotification(null)}
          className="invoice-notification"
        />
      )}

      <div className="invoice-container">
        <Tile className="invoice-form-tile">
          <div className="invoice-form-grid">
            <div className="invoice-section">
              <h3>Your Company</h3>
              <TextInput
                id="company-name"
                labelText="Company Name"
                value={invoice.companyName}
                onChange={(e) => setInvoice({ ...invoice, companyName: e.target.value })}
              />
              <TextArea
                id="company-address"
                labelText="Company Address"
                value={invoice.companyAddress}
                onChange={(e) => setInvoice({ ...invoice, companyAddress: e.target.value })}
                rows={2}
              />
              <TextInput
                id="company-email"
                type="email"
                labelText="Company Email"
                value={invoice.companyEmail}
                onChange={(e) => setInvoice({ ...invoice, companyEmail: e.target.value })}
              />
              <TextInput
                id="company-phone"
                labelText="Company Phone"
                value={invoice.companyPhone}
                onChange={(e) => setInvoice({ ...invoice, companyPhone: e.target.value })}
              />
            </div>

            <div className="invoice-section">
              <h3>Client Information</h3>
              <TextInput
                id="client-name"
                labelText="Client Name"
                value={invoice.clientName}
                onChange={(e) => setInvoice({ ...invoice, clientName: e.target.value })}
                required
              />
              <TextArea
                id="client-address"
                labelText="Client Address"
                value={invoice.clientAddress}
                onChange={(e) => setInvoice({ ...invoice, clientAddress: e.target.value })}
                rows={2}
              />
              <TextInput
                id="client-email"
                type="email"
                labelText="Client Email"
                value={invoice.clientEmail}
                onChange={(e) => setInvoice({ ...invoice, clientEmail: e.target.value })}
              />
            </div>

            <div className="invoice-section">
              <h3>Invoice Details</h3>
              <TextInput
                id="invoice-number"
                labelText="Invoice Number"
                value={invoice.invoiceNumber}
                onChange={(e) => setInvoice({ ...invoice, invoiceNumber: e.target.value })}
              />
              <CarbonDateField
                id="invoice-date"
                labelText="Invoice Date"
                value={invoice.date}
                onChange={(date) => setInvoice({ ...invoice, date })}
              />
              <CarbonDateField
                id="due-date"
                labelText="Due Date"
                value={invoice.dueDate}
                minDate={invoice.date || undefined}
                onChange={(dueDate) => setInvoice({ ...invoice, dueDate })}
              />
              <Select
                id="currency"
                labelText="Currency"
                value={invoice.currency}
                onChange={(e) => setInvoice({ ...invoice, currency: e.target.value })}
              >
                {currencies.map(c => (
                  <SelectItem key={c.value} value={c.value} text={c.label} />
                ))}
              </Select>
            </div>
          </div>

          <div className="invoice-items-section">
            <h3>Line Items</h3>
            <div className="invoice-items">
              {invoice.items.map((item, index) => (
                <div key={index} className="invoice-item-row">
                  <TextInput
                    id={`item-desc-${index}`}
                    labelText={index === 0 ? "Description" : ""}
                    placeholder="Item description"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    className="item-description"
                  />
                  <NumberInput
                    id={`item-qty-${index}`}
                    label={index === 0 ? "Qty" : ""}
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.imaginaryTarget.value) || 0)}
                    min={1}
                    className="item-quantity"
                  />
                  <NumberInput
                    id={`item-rate-${index}`}
                    label={index === 0 ? "Rate" : ""}
                    value={item.rate}
                    onChange={(e) => updateItem(index, 'rate', parseFloat(e.imaginaryTarget.value) || 0)}
                    min={0}
                    step={0.01}
                    className="item-rate"
                  />
                  <div className="item-amount">
                    <span className="amount-label">{index === 0 ? "Amount" : ""}</span>
                    <span className="amount-value">{formatCurrency(item.amount)}</span>
                  </div>
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={TrashCan}
                    onClick={() => removeItem(index)}
                    disabled={invoice.items.length === 1}
                    className="item-remove"
                  />
                </div>
              ))}
            </div>
            
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={Add}
              onClick={addItem}
              className="add-item-btn"
            >
              Add Item
            </Button>
          </div>

          <div className="invoice-totals-section">
            <div className="tax-input">
              <NumberInput
                id="tax-rate"
                label="Tax Rate (%)"
                value={invoice.taxRate}
                onChange={(e) => {
                  const rate = parseFloat(e.imaginaryTarget.value) || 0;
                  setInvoice({ ...invoice, taxRate: rate });
                  calculateTotals(invoice.items);
                }}
                min={0}
                max={100}
              />
            </div>
            <div className="totals-display">
              <div className="total-line">
                <span>Subtotal:</span>
                <span>{formatCurrency(invoice.subtotal)}</span>
              </div>
              <div className="total-line">
                <span>Tax ({invoice.taxRate}%):</span>
                <span>{formatCurrency(invoice.taxAmount)}</span>
              </div>
              <div className="total-line grand-total">
                <span>Total:</span>
                <span>{formatCurrency(invoice.total)}</span>
              </div>
            </div>
          </div>

          <TextArea
            id="notes"
            labelText="Notes / Terms"
            value={invoice.notes}
            onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })}
            rows={3}
            placeholder="Payment terms, bank details, or additional notes..."
            className="invoice-notes"
          />

          <div className="invoice-actions">
            <Button kind="primary" renderIcon={Download} onClick={generatePDF}>
              Generate PDF
            </Button>
            <Button kind="secondary" renderIcon={Send} onClick={sendInvoice} disabled={!invoice.clientEmail}>
              Send to Client
            </Button>
          </div>
        </Tile>
      </div>
    </div>
  );
};

export default InvoiceGenerator;
